import { useState } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useTabParams } from "@/contexts/TabParamsContext";
import { useTabLabel } from "@/hooks/useTabLabel";
import { useQuery } from "@tanstack/react-query";
import { NewProjectWizard, WizardEstimate } from "@/components/controller/NewProjectWizard";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Send, Copy, Eye, Clock, CheckCircle2, Pencil, ExternalLink, AlertTriangle, Ruler, Printer, GitBranch } from "lucide-react";
import { Estimate, isNewFormat, SubLine, useUpdateEstimate, DEFAULT_PRINT_SETTINGS, EstimatePrintSettings, EstimateGroup } from "@/hooks/useEstimates";
import { SendEstimateDialog } from "@/components/controller/SendEstimateDialog";
import { ConvertEstimateDialog } from "@/components/controller/ConvertEstimateDialog";
import { TakeoffWorkspace } from "@/components/controller/project-detail/TakeoffWorkspace";
import { NewEstimateDialog } from "@/components/controller/NewEstimateDialog";
import { ReviseEstimateDialog } from "@/components/controller/ReviseEstimateDialog";
import { DuplicateEstimateDialog } from "@/components/controller/DuplicateEstimateDialog";
import { isEstimateTakeoffLocked } from "@/lib/estimateLock";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { EstimatePrintTemplate, EstimatePrintData, EstimatePrintGroup, EstimatePrintLineItem } from "@/components/shared/print-templates/EstimatePrintTemplate";
import { exportPdf } from "@/lib/export";
import { useProposalPresets, findPresetForJobType } from "@/hooks/useEstimateProposalPresets";
import { parseLocation } from "@/lib/estimateProposal";

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
};

const safeDateFull = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; }
};

const statusClass: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Sent: "bg-blue-100 text-blue-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-destructive/15 text-destructive",
  Closed: "bg-muted text-muted-foreground",
  Converted: "bg-violet-100 text-violet-800",
  Pending: "bg-yellow-100 text-yellow-800",
};

const STATUSES = ["Draft", "Sent", "Accepted", "Rejected", "Closed", "Converted"];

const EstimateDetail = () => {
  const { id } = useTabParams<{ id: string }>();
  const navigate = useTabNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEstimate, setWizardEstimate] = useState<WizardEstimate | null>(null);
  const updateEstimate = useUpdateEstimate();
  const { data: presets } = useProposalPresets();

  // Fetch estimate with customer details and company info
  const { data: estimate, isLoading } = useQuery({
    queryKey: ["estimate_detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*, customers(name, contact_name, email, phone, billing_address, billing_city, billing_state, billing_zip)")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as unknown as Estimate & {
        sent_at?: string; sent_to_name?: string; sent_to_email?: string;
        viewed_at?: string; view_count?: number;
        approved_at?: string; approved_by_name?: string;
        rejected_at?: string; rejection_reason?: string;
        customer_notes?: string; share_token?: string;
        customers?: { name: string; contact_name?: string; email?: string; phone?: string; billing_address?: string; billing_city?: string; billing_state?: string; billing_zip?: string } | null;
      };
    },
    enabled: !!id,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Fetch company info for PDF + tax rate
  const { data: companyInfo } = useQuery({
    queryKey: ["company_info_for_pdf"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return null;
      const { data } = await supabase.from("companies").select("name, logo_url, address, city, state, zip, phone, website, material_purchase_tax_rate").eq("id", companyId).single();
      return data;
    },
  });

  // Dynamic tax rate: prefer customer billing state, fallback to company state
  const { data: dynamicTaxRate } = useQuery({
    queryKey: ["estimate_detail_tax_rate", estimate?.id, companyInfo?.state],
    queryFn: async () => {
      const cust = (estimate as any)?.customers;
      const stateCode = (cust?.billing_state || companyInfo?.state || "").toUpperCase();
      if (!stateCode) return 0;
      const { data: tr } = await supabase.from("state_tax_rates" as any).select("base_rate").eq("state_code", stateCode).maybeSingle();
      return ((tr as any)?.base_rate ?? 0) * 100;
    },
    enabled: !!estimate && !!companyInfo,
  });

  // Fetch revision siblings
  const rootId = estimate?.parent_estimate_id ?? estimate?.id;
  const { data: revisions } = useQuery({
    queryKey: ["estimate_revisions", rootId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estimates")
        .select("id, estimate_number, revision_number, status, created_at")
        .or(`id.eq.${rootId},parent_estimate_id.eq.${rootId}`)
        .order("revision_number", { ascending: true });
      return (data ?? []) as { id: string; estimate_number: string | null; revision_number: number; status: string; created_at: string }[];
    },
    enabled: !!rootId,
  });

  useTabLabel(estimate?.estimate_number ? estimate.estimate_number : undefined);

  if (isLoading) return <p className="text-muted-foreground p-8">Loading…</p>;
  if (!estimate) return <p className="text-destructive p-8">Estimate not found.</p>;

  const raw = estimate as any;
  const isLocked = isEstimateTakeoffLocked(estimate.status);
  const canConvert = estimate.status === "Accepted";
  const canSend = ["Draft", "Sent"].includes(estimate.status);
  const revNum = estimate.revision_number ?? 0;

  const convertEstimateData = {
    id: estimate.id,
    project_id: raw.project_id ?? null,
    is_change_order: raw.is_change_order ?? false,
    customer_id: raw.customer_id ?? null,
    customer_name: estimate.customers?.name ?? null,
    total_amount: estimate.total_amount,
    line_items: estimate.line_items as any[],
    estimate_number: estimate.estimate_number,
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateEstimate.mutateAsync({ id: estimate.id, status: newStatus } as any);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const copyShareLink = () => {
    if (raw.share_token) {
      navigator.clipboard.writeText(`${window.location.origin}/estimate/${raw.share_token}`);
      toast.success("Link copied!");
    }
  };

  // Calculate totals (must be before buildPrintData so tax values are in scope)
  let laborCost = 0, laborBilled = 0, materialCost = 0, materialBilled = 0;
  (estimate.line_items ?? []).forEach((item: any) => {
    if (isNewFormat(item)) {
      laborBilled += item.labor_total_billed ?? item.labor_total ?? 0;
      materialBilled += item.material_total_billed ?? item.material_total ?? 0;
      laborCost += item.labor_total_cost ?? 0;
      materialCost += item.material_total_cost ?? 0;
    } else if (Array.isArray(item.sub_lines)) {
      (item.sub_lines as SubLine[]).forEach((sl) => {
        const billed = sl.total_billed ?? sl.total ?? 0;
        const cost = sl.total_cost ?? 0;
        if (sl.type === "labor") { laborBilled += billed; laborCost += cost; }
        else { materialBilled += billed; materialCost += cost; }
      });
    } else {
      const qty = item.qty ?? item.Qty ?? 0;
      const lp = item.labor_price ?? 0;
      const mp = item.material_price ?? 0;
      const up = item.UnitPrice ?? item.unit_price ?? 0;
      if (item.line_type === "labor") laborBilled += qty * (lp || up);
      else if (item.line_type === "material") materialBilled += qty * (mp || up);
      else { laborBilled += qty * lp; materialBilled += qty * mp; if (!lp && !mp) laborBilled += qty * up; }
    }
  });

  const purchaseTaxRate = (companyInfo as any)?.material_purchase_tax_rate ?? 0;
  const vendorTaxOnMaterials = materialCost * (purchaseTaxRate / 100);
  const landedMaterialCost = materialCost + vendorTaxOnMaterials;
  const totalCost = laborCost + landedMaterialCost;
  const totalBilled = laborBilled + materialBilled;

  // Detect embedded-tax estimates: tax_amount stored as 0 and sub-lines have apply_vendor_tax
  const storedTaxRate = (estimate as any).tax_rate ?? 0;
  const storedTaxAmount = (estimate as any).tax_amount ?? 0;
  const taxRate = storedTaxRate > 0 ? storedTaxRate : (dynamicTaxRate ?? 0);

  const hasEmbeddedTax = storedTaxAmount === 0 && (estimate.line_items ?? []).some((item: any) =>
    Array.isArray(item.sub_lines) && (item.sub_lines as any[]).some((sl: any) => sl.apply_vendor_tax === true)
  );

  // For embedded-tax estimates, don't re-add tax — it's already in unit_price
  const taxAmount = hasEmbeddedTax ? 0 : (storedTaxAmount > 0 ? storedTaxAmount : (totalBilled * (taxRate / 100)));
  const totalWithTax = hasEmbeddedTax ? totalBilled : totalBilled + taxAmount;

  // Compute embedded tax total for informational display
  let embeddedTaxTotal = 0;
  if (hasEmbeddedTax) {
    (estimate.line_items ?? []).forEach((item: any) => {
      if (Array.isArray(item.sub_lines)) {
        (item.sub_lines as any[]).forEach((sl: any) => {
          if (sl.taxable && sl.apply_vendor_tax) {
            const t = (sl.vendor_tax_rate ?? 0) / 100;
            const c = sl.cost_price ?? 0;
            const m = (sl.markup_pct ?? 0) / 100;
            const effectiveCost = c * (1 + t);
            const preTaxPrice = effectiveCost * (1 + m);
            const customerTax = preTaxPrice * t;
            embeddedTaxTotal += customerTax * (sl.qty ?? 0);
          }
        });
      }
    });
  }

  const materialSubtotalPreTax = hasEmbeddedTax ? Math.max(materialBilled - embeddedTaxTotal, 0) : materialBilled;
  const salesTaxTotal = hasEmbeddedTax ? embeddedTaxTotal : taxAmount;

  const profit = totalBilled - totalCost;
  const margin = totalBilled > 0 ? (profit / totalBilled) * 100 : 0;
  const marginColor = margin >= 30 ? "text-emerald-600" : margin >= 20 ? "text-yellow-600" : "text-destructive";

  // Build print data
  const buildPrintData = (): EstimatePrintData => {
    const toPrintLine = (item: any, idx: number): EstimatePrintLineItem => {
      const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
      const qty = Number(item.qty ?? item.Qty ?? 0);
      let totalBilled = item.total_billed ?? item.total ?? item.Amount ?? 0;
      let laborBilled = 0;
      let materialBilled = 0;
      if (hasSubLines) {
        (item.sub_lines as any[]).forEach((sl: any) => {
          const b = sl.total_billed ?? sl.total ?? (sl.qty ?? 0) * (sl.unit_price ?? 0);
          if (sl.type === "labor") laborBilled += b;
          else materialBilled += b;
        });
        if (!totalBilled) totalBilled = laborBilled + materialBilled;
      }
      const unit = item.unit || item.UnitOfMeasure || "LS";
      const customerDesc = item.customer_description || item.description || item.Description || `Item ${idx + 1}`;
      const safeQty = qty || 1;
      return {
        line_number: Number(item.line_num ?? idx + 1),
        label: item.label || undefined,
        description: item.description || item.Description || customerDesc,
        customer_description: customerDesc,
        quantity: qty,
        unit,
        labor_unit_price: laborBilled / safeQty,
        material_unit_price: materialBilled / safeQty,
        unit_price: Number(totalBilled) / safeQty,
        amount: Number(totalBilled) || 0,
        location: parseLocation(customerDesc),
        sub_lines: Array.isArray(item.sub_lines)
          ? item.sub_lines.map((sl: any) => ({ type: sl.type, description: sl.description }))
          : undefined,
      };
    };

    const groups: EstimateGroup[] = (estimate.estimate_groups ?? []) as EstimateGroup[];
    const items = (estimate.line_items ?? []) as any[];
    let printGroups: EstimatePrintGroup[] | undefined;
    let flatLines: EstimatePrintLineItem[] | undefined;

    if (groups.length > 0) {
      const sorted = [...groups].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      printGroups = sorted
        .map((g) => {
          const groupItems = items.filter((it: any) => it.group_id === g.id);
          if (groupItems.length === 0) return null;
          const lines = groupItems.map((it: any, i: number) => toPrintLine(it, i));
          const subtotal = lines.reduce((s, l) => s + l.amount, 0);
          return { name: g.name || "Untitled Section", color: g.color, lines, subtotal } as EstimatePrintGroup;
        })
        .filter((x): x is EstimatePrintGroup => x !== null);
      const ungrouped = items.filter((it: any) => !it.group_id || !groups.some((g) => g.id === it.group_id));
      if (ungrouped.length > 0) {
        const lines = ungrouped.map((it: any, i: number) => toPrintLine(it, i));
        printGroups.push({ name: "General Scope", lines, subtotal: lines.reduce((s, l) => s + l.amount, 0) });
      }
    } else {
      flatLines = items.map((it: any, i: number) => toPrintLine(it, i));
    }

    const subtotal = estimate.subtotal ?? estimate.total_amount ?? 0;
    const settings: EstimatePrintSettings = estimate.print_settings ?? DEFAULT_PRINT_SETTINGS;
    const preset = findPresetForJobType(presets ?? [], estimate.job_type);

    const cust = estimate.customers as any;
    const custAddr = cust ? [cust.billing_address, cust.billing_city, cust.billing_state, cust.billing_zip].filter(Boolean).join(", ") : undefined;

    return {
      estimate_number: estimate.estimate_number ?? "—",
      estimate_date: estimate.txn_date ?? new Date().toISOString(),
      expiry_date: estimate.expiry_date ?? null,
      status: estimate.status,
      customer_name: cust?.name ?? undefined,
      customer_contact_name: cust?.contact_name ?? undefined,
      customer_phone: cust?.phone ?? undefined,
      customer_email: cust?.email ?? undefined,
      project_name: estimate.project_name ?? undefined,
      project_address: custAddr,
      company_name: companyInfo?.name,
      company_logo_url: (companyInfo as any)?.logo_url ?? null,
      company_address: companyInfo?.address,
      company_city_state_zip: [companyInfo?.city, companyInfo?.state, companyInfo?.zip].filter(Boolean).join(", ") || undefined,
      company_phone: companyInfo?.phone,
      company_website: companyInfo?.website,
      groups: printGroups,
      flat_lines: flatLines,
      subtotal,
      tax_amount: hasEmbeddedTax ? 0 : taxAmount,
      tax_rate: taxRate,
      total: hasEmbeddedTax ? subtotal : subtotal + taxAmount,
      labor_subtotal: laborBilled,
      material_subtotal_pre_tax: materialSubtotalPreTax,
      sales_tax_total: salesTaxTotal,
      notes: estimate.notes ?? undefined,
      settings,
      clarifications: preset?.clarifications ?? undefined,
      exclusions: preset?.exclusions ?? undefined,
      acceptance_text: preset?.acceptance_text ?? undefined,
    };
  };

  const handlePrint = () => {
    const data = buildPrintData();
    exportPdf(<EstimatePrintTemplate data={data} />, `Estimate ${data.estimate_number}`);
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/controller/estimates")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              {estimate.estimate_number || "Estimate"}
            </h1>
            {revNum > 0 && (
              <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                Rev {revNum}
              </Badge>
            )}
            <Badge variant="outline" className={statusClass[estimate.status] ?? "bg-muted text-muted-foreground"}>
              {estimate.status}
            </Badge>
            {isLocked && (
              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800">Read-Only</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-12 md:ml-0">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-3.5 w-3.5 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={() => setReviseOpen(true)}>
            <GitBranch className="h-3.5 w-3.5 mr-1" /> Revise
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDuplicateOpen(true)}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
          </Button>
          {estimate.status === "Draft" && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
          {canSend && (
            <Button variant="outline" size="sm" onClick={() => setSendOpen(true)}>
              <Send className="h-3.5 w-3.5 mr-1" /> Send
            </Button>
          )}
          {canConvert && (
            <Button size="sm" onClick={() => setConvertOpen(true)}>
              Convert <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
          <Select value={estimate.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[130px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="takeoff" className="gap-1.5">
            <Ruler className="h-3.5 w-3.5" />Plans & Takeoff
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          {/* Approved Banner */}
          {estimate.status === "Accepted" && raw.approved_by_name && (
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800 dark:text-emerald-400">Approved by {raw.approved_by_name}</span>
              </div>
              <p className="text-sm text-muted-foreground">{safeDateFull(raw.approved_at ?? null)}</p>
              {raw.customer_notes && <p className="text-sm italic">"{raw.customer_notes}"</p>}
            </div>
          )}

          {/* Rejected Banner */}
          {estimate.status === "Rejected" && (
            <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5 space-y-1">
              <p className="font-semibold text-destructive">❌ Rejected{raw.approved_by_name ? ` by ${raw.approved_by_name}` : ""}</p>
              <p className="text-sm text-muted-foreground">{safeDateFull(raw.rejected_at ?? null)}</p>
              {raw.rejection_reason && <p className="text-sm italic">"{raw.rejection_reason}"</p>}
            </div>
          )}

          {/* Tracking Panel */}
          {raw.sent_at && (
            <div className="p-3 rounded-lg border bg-muted/30 space-y-2 text-sm">
              <p className="font-semibold flex items-center gap-1"><Send className="h-3 w-3" /> Sent to Customer</p>
              <p className="text-muted-foreground">{raw.sent_to_name} — {raw.sent_to_email}</p>
              <p className="text-muted-foreground">Sent: {safeDateFull(raw.sent_at)}</p>
              {raw.viewed_at && (
                <p className="flex items-center gap-1 text-muted-foreground">
                  <Eye className="h-3 w-3" /> Viewed: {safeDateFull(raw.viewed_at)}
                  {(raw.view_count ?? 0) > 1 && <span>(viewed {raw.view_count} times)</span>}
                </p>
              )}
              {estimate.status === "Sent" && (
                <p className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" /> Awaiting response
                </p>
              )}
              <div className="flex gap-2 mt-1">
                {canSend && <Button variant="outline" size="sm" onClick={() => setSendOpen(true)}>Resend</Button>}
                {raw.share_token && <Button variant="outline" size="sm" onClick={copyShareLink}><Copy className="h-3 w-3 mr-1" /> Copy Link</Button>}
              </div>
            </div>
          )}

          {/* Customer / Project / Date */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{estimate.customers?.name ?? "—"}</p></div>
            <div><span className="text-muted-foreground">Project</span><p className="font-medium">{estimate.project_name ?? "—"}</p></div>
            <div><span className="text-muted-foreground">Date</span><p className="font-medium">{safeDate(estimate.txn_date)}</p></div>
            <div><span className="text-muted-foreground">Expiry</span><p className="font-medium">{safeDate(estimate.expiry_date ?? null)}</p></div>
          </div>

          {/* Revision History */}
          {revisions && revisions.length > 1 && (
            <>
              <Separator />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Revision History</h3>
                <div className="space-y-1">
                  {revisions.map((rev) => (
                    <div
                      key={rev.id}
                      className={cn(
                        "flex items-center gap-2 text-sm px-2 py-1 rounded cursor-pointer hover:bg-muted/50",
                        rev.id === estimate.id && "bg-muted font-medium"
                      )}
                      onClick={() => {
                        if (rev.id !== estimate.id) navigate(`/controller/estimates/${rev.id}`);
                      }}
                    >
                      <Badge variant="outline" className="text-xs">
                        {rev.revision_number === 0 ? "Original" : `Rev ${rev.revision_number}`}
                      </Badge>
                      <span className="text-muted-foreground">{rev.estimate_number ?? "—"}</span>
                      <Badge variant="outline" className={cn("text-xs ml-auto", statusClass[rev.status])}>
                        {rev.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{safeDate(rev.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Revision Notes */}
          {estimate.revision_notes && (
            <div className="p-3 rounded-lg border bg-violet-50/50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800 text-sm">
              <p className="font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-1 mb-1">
                <GitBranch className="h-3 w-3" /> Revision Notes
              </p>
              <p className="text-muted-foreground whitespace-pre-wrap">{estimate.revision_notes}</p>
            </div>
          )}

          <Separator />

          {/* Line Items */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Line Items</h3>
            {(estimate.line_items ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No line items</p>
            ) : (
              (estimate.line_items as any[]).map((item: any, idx: number) => {
                const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
                const desc = item.description || item.Description || `Line ${idx + 1}`;
                const qty = item.qty ?? item.Qty ?? 0;
                const unit = item.unit || item.UnitOfMeasure || "";
                const lineBilled = item.total_billed ?? item.total ?? item.Amount ?? (qty * (item.UnitPrice ?? item.unit_price ?? 0));

                return (
                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono w-6">#{item.line_num ?? idx + 1}</span>
                        {item.label && (
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-mono font-semibold bg-muted border border-border/60">
                            {item.label}
                          </span>
                        )}
                        <span className="font-medium text-sm">{desc}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground">{qty} {unit}</span>
                        <span className="font-semibold">{fmt(lineBilled)}</span>
                      </div>
                    </div>
                    {hasSubLines && (item.sub_lines as any[]).map((sl: any, si: number) => (
                      <div key={sl.id ?? si} className="flex items-center gap-2 pl-8 text-sm">
                        <Badge variant="outline" className={`text-xs ${sl.type === "labor" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {sl.type === "labor" ? "Labor" : "Matl"}
                        </Badge>
                        <span className="flex-1 truncate">{sl.description}</span>
                        <span className="text-muted-foreground">{sl.qty} {sl.unit}</span>
                        <span className="text-muted-foreground">@ {fmt(sl.unit_price)}</span>
                        <span className="font-medium">{fmt(sl.total_billed ?? sl.total ?? 0)}</span>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          {/* Internal Cost Summary */}
          {totalCost > 0 && (
            <>
              <Separator />
              <div className="border rounded-lg p-3 space-y-1 bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                  <AlertTriangle className="h-3 w-3" /> INTERNAL
                </div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Labor Cost</span><span>{fmt(laborCost)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Material Cost (raw)</span><span>{fmt(materialCost)}</span></div>
                {vendorTaxOnMaterials > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Vendor Tax ({purchaseTaxRate}%)</span><span>{fmt(vendorTaxOnMaterials)}</span></div>
                )}
                {vendorTaxOnMaterials > 0 && (
                  <div className="flex justify-between text-sm font-medium"><span className="text-muted-foreground">Landed Material Cost</span><span>{fmt(landedMaterialCost)}</span></div>
                )}
                <div className="flex justify-between text-sm border-t pt-1 mt-1"><span className="text-muted-foreground">Total Cost</span><span>{fmt(totalCost)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Revenue</span><span>{fmt(totalBilled)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Gross Profit</span><span className={marginColor}>{fmt(profit)}</span></div>
                <div className="flex justify-between text-sm font-semibold"><span className="text-muted-foreground">Margin</span><span className={marginColor}>{margin.toFixed(1)}%</span></div>
              </div>
            </>
          )}

          {estimate.notes && (
            <><Separator /><div><h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Notes</h3><p className="text-sm whitespace-pre-wrap">{estimate.notes}</p></div></>
          )}

          <Separator />
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Labor (billed)</span><span>{fmt(laborBilled)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Material (billed)</span><span>{fmt(materialBilled)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmt(totalBilled)}</span></div>
            {!hasEmbeddedTax && taxAmount > 0 && (
              <div className="flex justify-between"><span className="text-muted-foreground">Sales Tax ({taxRate}%)</span><span>{fmt(taxAmount)}</span></div>
            )}
            {hasEmbeddedTax && embeddedTaxTotal > 0 && (
              <div className="flex justify-between text-muted-foreground italic text-xs"><span>Includes Sales Tax ({taxRate}%)</span><span>{fmt(embeddedTaxTotal)}</span></div>
            )}
            <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
              <span>Grand Total</span><span>{fmt(totalWithTax)}</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="takeoff" className="mt-0">
          <TakeoffWorkspace
            ownerType="estimate"
            ownerId={id!}
            readOnly={isLocked}
          />
        </TabsContent>
      </Tabs>

      <SendEstimateDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        estimate={estimate}
        onSent={() => setSendOpen(false)}
      />
      <ConvertEstimateDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        estimate={convertEstimateData}
        onNewProject={() => {
          setWizardEstimate({
            id: estimate.id,
            doc_number: estimate.estimate_number ?? null,
            customer_qb_id: null,
            customer_ref_name: estimate.customers?.name ?? null,
            total_amount: estimate.total_amount ?? null,
            line_items: estimate.line_items as any[] ?? null,
          });
          setWizardOpen(true);
        }}
      />
      <NewProjectWizard open={wizardOpen} onOpenChange={setWizardOpen} initialEstimate={wizardEstimate} />
      <NewEstimateDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editEstimate={estimate}
      />
      <ReviseEstimateDialog
        open={reviseOpen}
        onOpenChange={setReviseOpen}
        estimate={estimate}
        onCreated={(newId) => navigate(`/controller/estimates/${newId}`)}
      />
      <DuplicateEstimateDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        sourceEstimate={estimate}
      />
    </div>
  );
};

export default EstimateDetail;
